import { pgaComTld } from "../../../utils"
import { ADDITIONAL_REQUIREMENTS } from "./constants/additionalRequirements"
import { REQUIREMENT } from "./constants/levels"
import { STATUS } from "./constants/statuses"
import { CHANGE_FORM_URLS } from "./constants/externalUrls"

export const ADDITIONAL_REQUIREMENT_KEY = {
  EXPERIENCE_CREDITS: "experience_credits",
  EMPLOYMENT: "employment",
  PAT: REQUIREMENT.PLAYING_ABILITY_TEST,
  BACKGROUND_CHECK: REQUIREMENT.BACKGROUND_CHECK,
  AGE: "age",
  GOOD_STANDING: "good_standing",
  ACCEPTABLE_PROGRESS: "acceptable_progress",
}

export const JOURNEY_STANDING_SUSPENSION_CODES = ["GPTP", "NPDU", "COEV"]

const DEFAULT_CREDIT_THRESHOLD = 28

const isExplicitlyIncomplete = criterion => criterion?.complete === false

export const hasJourneyStandingSuspension = (suspensions = []) =>
  suspensions.some(suspension =>
    JOURNEY_STANDING_SUSPENSION_CODES.includes(suspension?.code),
  )

export const membershipDuesUrl = () => `https://${pgaComTld}/member/dues`

const isEligibilityPatComplete = eligibility => {
  const pat = eligibility?.playingAbilityTest
  return pat?.complete === true && !pat?.expired
}

const isPatOutstanding = patStatus =>
  Boolean(patStatus) && patStatus !== STATUS.COMPLETE

const patIncompleteDescription = ({ eligibility, patStatus }) => {
  const patConfig = ADDITIONAL_REQUIREMENTS.PAT

  if (patStatus === STATUS.QUALIFIED) {
    return patConfig.qualifiedDescription
  }

  if (
    patStatus === STATUS.EXPIRED ||
    eligibility?.playingAbilityTest?.expired
  ) {
    return patConfig.expiredDescription
  }

  return patConfig.incompleteDescription
}

export const formatExperienceCreditsDescription = (credits = {}) => {
  const total = credits.total ?? 0
  const threshold = credits.threshold ?? DEFAULT_CREDIT_THRESHOLD
  const education = credits.educationCredits
  const employment = credits.employmentCredits
  const hasSplit = education != null && employment != null
  const split = hasSplit
    ? ` (${education} education, ${employment} employment)`
    : ""

  return `You have ${total} of the ${threshold} credits you need${split}.`
}

const buildExperienceCreditsExplainerItem = () => {
  const { title, explainer, linkText, linkUrl } =
    ADDITIONAL_REQUIREMENTS.EXPERIENCE_CREDITS

  return {
    key: ADDITIONAL_REQUIREMENT_KEY.EXPERIENCE_CREDITS,
    complete: false,
    countsTowardCompletion: false,
    hasWarning: false,
    title,
    description: explainer,
    secondaryLinkText: linkText,
    secondaryLinkUrl: linkUrl,
  }
}

const buildBackgroundCheckItem = ({
  eligibility,
  useStudentStyleChecklist,
}) => {
  const backgroundCheckConfig = ADDITIONAL_REQUIREMENTS.BACKGROUND_CHECK

  if (useStudentStyleChecklist) {
    const backgroundCheckComplete =
      eligibility?.backgroundCheck?.complete === true

    return {
      key: ADDITIONAL_REQUIREMENT_KEY.BACKGROUND_CHECK,
      complete: backgroundCheckComplete,
      countsTowardCompletion: true,
      hasWarning: !backgroundCheckComplete,
      title: backgroundCheckConfig.title,
      description: backgroundCheckComplete
        ? backgroundCheckConfig.onFileCompletedDescription
        : backgroundCheckConfig.onFileDescription,
      ...(backgroundCheckComplete
        ? {}
        : {
            primaryLinkKind: "in-app",
            primaryLinkText: backgroundCheckConfig.primaryLinkText,
            primaryLinkUrl: backgroundCheckConfig.primaryLinkUrl,
          }),
    }
  }

  const backgroundCheck = eligibility?.backgroundCheck
  if (!backgroundCheck) {
    return null
  }

  if (!isExplicitlyIncomplete(backgroundCheck)) {
    return null
  }

  return {
    key: ADDITIONAL_REQUIREMENT_KEY.BACKGROUND_CHECK,
    complete: false,
    countsTowardCompletion: true,
    hasWarning: true,
    title: backgroundCheckConfig.title,
    description: backgroundCheckConfig.description,
    primaryLinkKind: "in-app",
    primaryLinkText: backgroundCheckConfig.primaryLinkText,
    primaryLinkUrl: backgroundCheckConfig.primaryLinkUrl,
  }
}

const buildLegacyPatWarningItem = ({ patStatus }) => {
  const patConfig = ADDITIONAL_REQUIREMENTS.PAT

  return {
    key: ADDITIONAL_REQUIREMENT_KEY.PAT,
    complete: false,
    countsTowardCompletion: false,
    hasWarning: true,
    title: patConfig.title,
    description: patIncompleteDescription({ patStatus }),
    primaryLinkKind: "pat-search",
    primaryLinkText: patConfig.primaryLinkText,
    secondaryLinkText: patConfig.secondaryLinkText,
    secondaryLinkUrl: patConfig.secondaryLinkUrl,
  }
}

export const buildAdditionalRequirementItems = ({
  eligibility,
  patStatus,
  suspensions = [],
  isPgmGraduate = false,
  useStudentStyleChecklist = false,
}) => {
  const patOutstanding = isPatOutstanding(patStatus)

  if (!eligibility) {
    const items = []

    if (patOutstanding) {
      items.push(buildLegacyPatWarningItem({ patStatus }))
    }

    items.push(buildExperienceCreditsExplainerItem())
    return items
  }

  const items = []
  const studentStyleChecklist = useStudentStyleChecklist || isPgmGraduate

  const patComplete = !patOutstanding && isEligibilityPatComplete(eligibility)
  const patConfig = ADDITIONAL_REQUIREMENTS.PAT

  items.push({
    key: ADDITIONAL_REQUIREMENT_KEY.PAT,
    complete: patComplete,
    countsTowardCompletion: true,
    hasWarning: !patComplete,
    title: patConfig.title,
    description: patComplete
      ? patConfig.completedDescription
      : patIncompleteDescription({ eligibility, patStatus }),
    ...(patComplete
      ? {}
      : {
          primaryLinkKind: "pat-search",
          primaryLinkText: patConfig.primaryLinkText,
          secondaryLinkText: patConfig.secondaryLinkText,
          secondaryLinkUrl: patConfig.secondaryLinkUrl,
        }),
  })

  const backgroundCheckItem = buildBackgroundCheckItem({
    eligibility,
    useStudentStyleChecklist: studentStyleChecklist,
  })

  if (backgroundCheckItem) {
    items.push(backgroundCheckItem)
  }

  const credits = eligibility.experienceCredits
  const creditsMissing = credits == null
  const creditsComplete = credits?.complete === true
  const creditsConfig = ADDITIONAL_REQUIREMENTS.EXPERIENCE_CREDITS

  items.push({
    key: ADDITIONAL_REQUIREMENT_KEY.EXPERIENCE_CREDITS,
    complete: creditsComplete,
    countsTowardCompletion: true,
    hasWarning: !creditsComplete,
    title: creditsConfig.title,
    description: creditsComplete
      ? creditsConfig.completedDescription
      : creditsMissing
        ? creditsConfig.explainer
        : formatExperienceCreditsDescription(credits),
    ...(creditsComplete
      ? {}
      : {
          secondaryLinkText: creditsConfig.linkText,
          secondaryLinkUrl: creditsConfig.linkUrl,
        }),
  })

  const employmentComplete = eligibility.activelyEmployed?.complete === true
  const employmentConfig = ADDITIONAL_REQUIREMENTS.EMPLOYMENT

  if (!studentStyleChecklist) {
    items.push({
      key: ADDITIONAL_REQUIREMENT_KEY.EMPLOYMENT,
      complete: employmentComplete,
      countsTowardCompletion: true,
      hasWarning: !employmentComplete,
      title: employmentConfig.title,
      description: employmentComplete
        ? employmentConfig.completedDescription
        : employmentConfig.description,
      ...(employmentComplete
        ? {}
        : {
            primaryLinkText: employmentConfig.primaryLinkText,
            primaryLinkUrl: CHANGE_FORM_URLS.NEW_EMPLOYMENT,
          }),
    })
  }

  if (!studentStyleChecklist && !hasJourneyStandingSuspension(suspensions)) {
    const duesComplete = eligibility.goodStanding?.complete === true
    const duesConfig = ADDITIONAL_REQUIREMENTS.GOOD_STANDING

    items.push({
      key: ADDITIONAL_REQUIREMENT_KEY.GOOD_STANDING,
      complete: duesComplete,
      countsTowardCompletion: true,
      hasWarning: !duesComplete,
      title: duesConfig.title,
      description: duesComplete
        ? duesConfig.completedDescription
        : duesConfig.description,
      ...(duesComplete
        ? {}
        : {
            primaryLinkText: duesConfig.primaryLinkText,
            primaryLinkUrl: membershipDuesUrl(),
          }),
    })
  }

  if (isExplicitlyIncomplete(eligibility.age)) {
    items.push({
      key: ADDITIONAL_REQUIREMENT_KEY.AGE,
      complete: false,
      countsTowardCompletion: true,
      hasWarning: true,
      title: ADDITIONAL_REQUIREMENTS.AGE.title,
      description: ADDITIONAL_REQUIREMENTS.AGE.description,
    })
  }

  return items
}
